// Which font a run of text is set in, and where that font comes from (the browser twin of rd_pdf::fontres).
//
// A PDF font is one of four things, and the resolver treats each the way a careful viewer would:
//   1. an embedded font program (usually a subset: it has only the letters the file used, so newly typed letters need another face);
//   2. one of the standard 14 names (Helvetica, Times, Courier ...), which a file never embeds: the metric-compatible open fonts
//      (Liberation Sans, Serif, Mono) stand in, so line breaks stay where they were;
//   3. a font named but not embedded (Calibri, Verdana ...): the same font when the browser lists it among the installed fonts
//      (Chrome and Edge, with permission);
//   4. anything else: the closest bundled open font (Liberation, Noto Sans) and, only when the person turned it on, a free font
//      fetched from Google Fonts' open-licence tree.
// Whatever is chosen, a FontStack adds a per-glyph fallback chain so a character the face lacks is drawn from the next face that has it
// instead of an empty box. A face whose licence forbids embedding is replaced by the closest free one, with a notice.

import { TrueTypeFont, bundledByClass, isStandard14, metricTwin, stripSubsetTag, cleanFamily, wishFromName, type ProgramKind } from "@fg/web-core/pdf";
import { localFontRecords, loadLocalFonts, localFontsSupported, type LocalFontData } from "@fg/ui-theme";

// ---- the bundled set -------------------------------------------------------------------------------------------------------------------

/** The bundled families (SIL OFL 1.1), served from /fonts as plain assets and fetched only when a face is needed. About 5.5 MB in all. */
export const BUNDLED_FAMILIES = ["Liberation Sans", "Liberation Serif", "Liberation Mono", "Noto Sans"] as const;
export type BundledFamily = (typeof BUNDLED_FAMILIES)[number];
export const isBundledFamily = (f: string): f is BundledFamily => (BUNDLED_FAMILIES as readonly string[]).includes(f);

const FILE: Record<BundledFamily, string> = { "Liberation Sans": "LiberationSans", "Liberation Serif": "LiberationSerif", "Liberation Mono": "LiberationMono", "Noto Sans": "NotoSans" };

export function bundledUrl(family: BundledFamily, bold: boolean, italic: boolean): string {
    // Noto Sans has no bold italic here: bold stands in
    const style = family === "Noto Sans" && bold && italic ? "Bold" : bold && italic ? "BoldItalic" : bold ? "Bold" : italic ? "Italic" : "Regular";
    return `${import.meta.env.BASE_URL ?? "/"}fonts/${FILE[family]}-${style}.ttf`.replace(/\/{2,}/g, "/");
}

const faceCache = new Map<string, Promise<Uint8Array | null>>();
const cached = (key: string, make: () => Promise<Uint8Array | null>) => { let p = faceCache.get(key); if (!p) { p = make().catch(() => null); faceCache.set(key, p); } return p; };

export const loadBundled = (family: BundledFamily, bold: boolean, italic: boolean): Promise<Uint8Array | null> =>
    cached(`b|${family}|${bold}|${italic}`, async () => { const r = await fetch(bundledUrl(family, bold, italic)); return r.ok ? new Uint8Array(await r.arrayBuffer()) : null; });

const shownBundled = new Set<string>();
/** Makes the browser draw a bundled family (the editor shows text in it before the file is saved). */
export async function showBundled(family: BundledFamily): Promise<void> {
    if (shownBundled.has(family) || typeof FontFace === "undefined") return;
    shownBundled.add(family);
    await Promise.all(([[false, false], [true, false], [false, true], [true, true]] as const).map(async ([bold, italic]) => {
        try { const f = new FontFace(family, `url(${bundledUrl(family, bold, italic)})`, { weight: bold ? "700" : "400", style: italic ? "italic" : "normal" }); document.fonts.add(await f.load()); } catch { /* the fallback family draws instead */ }
    }));
}

// ---- the optional download from Google Fonts' open-licence tree ---------------------------------------------------------------------------

const GOOGLE_KEY = "fg.pdf.fonts.google";
export function googleFontsOn(): boolean { try { return localStorage.getItem(GOOGLE_KEY) === "1"; } catch { return false; } }
export function setGoogleFonts(on: boolean): void { try { if (on) localStorage.setItem(GOOGLE_KEY, "1"); else localStorage.removeItem(GOOGLE_KEY); } catch { /* the setting is just not kept */ } }

/** Where Google Fonts keeps a family's static files (the open-licence tree of the google/fonts repository). */
export function googleFontUrls(family: string, bold: boolean, italic: boolean): string[] {
    const clean = family.replace(/[^A-Za-z0-9]/g, "");
    const style = bold && italic ? "BoldItalic" : bold ? "Bold" : italic ? "Italic" : "Regular";
    const base = `https://raw.githubusercontent.com/google/fonts/main/ofl/${clean.toLowerCase()}`;
    const urls = [`${base}/${clean}-${style}.ttf`];
    if (!bold && !italic) urls.push(`${base}/${clean}%5Bwght%5D.ttf`, `${base}/${clean}%5Bwdth%2Cwght%5D.ttf`);
    return urls;
}

/** The family's face from the browser cache or fetched. Only when the person turned the download on; the bytes must parse as a font and be licensed for embedding. */
export async function loadGoogle(family: string, bold: boolean, italic: boolean): Promise<Uint8Array | null> {
    if (!googleFontsOn() || !family.trim()) return null;
    return cached(`g|${family}|${bold}|${italic}`, async () => {
        for (const url of googleFontUrls(family, bold, italic)) {
            try {
                const r = await fetch(url, { cache: "force-cache" });
                if (!r.ok) continue;
                const b = new Uint8Array(await r.arrayBuffer());
                const info = faceInfo(b);
                if (info?.canEmbed) return b;
            } catch { /* the next address, or nothing */ }
        }
        return null;
    });
}

// ---- what a face says about itself ---------------------------------------------------------------------------------------------------------

export interface FaceInfo { canEmbed: boolean; canSubset: boolean; hasCmap: boolean; outlines: boolean }

const sfntOk = (b: Uint8Array) => b.length > 12 && (String.fromCharCode(b[0]!, b[1]!, b[2]!, b[3]!) === "true" || (b[0] === 0 && b[1] === 1 && b[2] === 0 && b[3] === 0));

/** Reads the licence (OS/2 fsType) and shape of a TrueType face; null when it is not one this code can embed (CFF outlines, damaged files). */
export function faceInfo(sfnt: Uint8Array): FaceInfo | null {
    if (!sfntOk(sfnt)) return null;
    try {
        const f = new TrueTypeFont(sfnt);
        const os2 = f.tableData("OS/2");
        const fsType = os2 && os2.length >= 10 ? (os2[8]! << 8) | os2[9]! : 0;
        const restricted = (fsType & 0x0f) === 2;
        const bitmapOnly = (fsType & 0x0200) !== 0;
        return { canEmbed: !restricted && !bitmapOnly, canSubset: (fsType & 0x0100) === 0, hasCmap: f.cmapCount > 0, outlines: f.hasOutlines };
    } catch { return null; }
}

/** Whether the face has a glyph for every non-space character of `text`. */
export function covers(sfnt: Uint8Array, text: string): boolean {
    try {
        const f = new TrueTypeFont(sfnt);
        for (const ch of text) { const cp = ch.codePointAt(0)!; if (cp <= 32 || cp === 0xa0 || (cp >= 0x7f && cp < 0xa0)) continue; if (f.gidByUnicode(cp) <= 0) return false; }
        return true;
    } catch { return false; }
}

// ---- installed fonts -------------------------------------------------------------------------------------------------------------------------

/** Lists the installed fonts when the person already allowed it (no prompt). True when the list is there. */
export async function ensureLocalFonts(prompt = false): Promise<boolean> {
    if (!localFontsSupported()) return false;
    if (localFontRecords("Arial") || localFontRecords("Calibri") || localFontRecords("Segoe UI")) return true;
    try {
        const st = await (navigator as unknown as { permissions: { query(o: { name: string }): Promise<{ state: string }> } }).permissions.query({ name: "local-fonts" });
        if (st.state === "granted" || (prompt && st.state === "prompt")) { await loadLocalFonts(); return true; }
    } catch { /* not asked */ }
    return false;
}

export async function installedProgram(family: string, bold: boolean, italic: boolean): Promise<Uint8Array | null> {
    const list: LocalFontData[] | undefined = localFontRecords(family);
    if (!list?.length) return null;
    const score = (f: LocalFontData) => { const s = f.style.toLowerCase(); return (/bold|black|heavy/.test(s) === bold ? 2 : 0) + (/italic|oblique/.test(s) === italic ? 2 : 0) + (/condensed|narrow|light|thin|medium/.test(s) ? 0 : 1); };
    const best = [...list].sort((a, b) => score(b) - score(a))[0]!;
    const bytes = new Uint8Array(await (await best.blob()).arrayBuffer());
    return sfntOk(bytes) ? bytes : null; // CFF-based .otf can't be embedded as TrueType
}

// ---- the choice ----------------------------------------------------------------------------------------------------------------------------

export type FontSource = "embedded" | "installed" | "bundled" | "downloaded";

export interface RunFontInfo {
    baseFont: string;
    bold: boolean;
    italic: boolean;
    flags?: number;
    /** The document's own program for the font. */
    program?: { kind: ProgramKind; bytes: Uint8Array | null };
    subset?: boolean;
}

export interface FontChoice {
    source: FontSource;
    /** The family the run asked for (no subset tag, no style words). */
    requested: string;
    /** The family that is used. */
    family: string;
    bold: boolean;
    italic: boolean;
    /** The face (null only when nothing could be loaded: the file then keeps a standard font). */
    sfnt: Uint8Array | null;
    /** The family is the one asked for, or its metric twin. */
    familyMatch: boolean;
    /** What to tell the person when the look cannot be kept. */
    notice: string | null;
}

const label = (c: FontChoice) => (c.requested && c.requested.toLowerCase() !== c.family.toLowerCase() ? `${c.requested} → ${c.family} (${c.source})` : `${c.family} (${c.source})`);
export const choiceLabel = label;

/** Resolves the font of a run that is about to show `text` (undefined: any text). */
export async function resolveFont(run: RunFontInfo, text?: string): Promise<FontChoice> {
    const requested = cleanFamily(run.baseFont);
    const wish = wishFromName(run.baseFont, run.flags ?? 0);
    const bold = wish.bold || run.bold, italic = wish.italic || run.italic;
    let why: string | null = null;

    // 1. the font program the document carries
    if (run.program?.kind === "sfnt" && run.program.bytes) {
        const info = faceInfo(run.program.bytes);
        const has = text === undefined ? true : !!info?.hasCmap && covers(run.program.bytes, text);
        if (info && !info.canEmbed) why = `The font "${requested}" does not permit embedding, so the closest free font is used.`;
        else if (info && info.canSubset && has) return { source: "embedded", requested, family: requested, bold, italic, sfnt: run.program.bytes, familyMatch: true, notice: null };
        else if (info && !has) why = run.subset ? `The font "${requested}" in this file only holds the letters it already used, so the new letters are set in another face.` : `The font "${requested}" lacks some of the letters typed.`;
    }

    const std14 = isStandard14(run.baseFont);
    const twin = metricTwin(run.baseFont);

    // 2. the standard 14 and the names with a metric twin: the bundled twin keeps every line break where it was
    if (std14 && twin && !wish.symbol) {
        const b = await loadBundled(twin, bold, italic);
        if (b) return { source: "bundled", requested, family: twin, bold, italic, sfnt: b, familyMatch: true, notice: why };
    }

    // 3. named but not embedded: the same font, installed on this computer
    if (!std14 && requested) {
        await ensureLocalFonts();
        const inst = await installedProgram(requested, bold, italic);
        if (inst) {
            const info = faceInfo(inst);
            if (info?.canEmbed && info.canSubset && (text === undefined || covers(inst, text))) return { source: "installed", requested, family: requested, bold, italic, sfnt: inst, familyMatch: true, notice: why };
            if (info && !info.canEmbed) why = `"${requested}" does not permit embedding in a document, so the closest free font is used.`;
        }
    }

    // 4. opt-in: a free font of that family from Google Fonts
    if (!std14 && requested && googleFontsOn()) {
        const g = await loadGoogle(requested, bold, italic);
        if (g && (text === undefined || covers(g, text))) return { source: "downloaded", requested, family: requested, bold, italic, sfnt: g, familyMatch: true, notice: why };
    }

    // 5. the closest bundled open font
    const fam: BundledFamily = twin ?? bundledByClass(wish);
    const sfnt = await loadBundled(fam, bold, italic);
    const familyMatch = twin !== null || std14;
    const notice = why ?? (familyMatch || !requested ? null : `The font "${requested}" is not available here, so the text is set in ${fam} instead.`);
    return { source: "bundled", requested, family: fam, bold, italic, sfnt, familyMatch, notice };
}

// ---- the per-glyph fallback chain ---------------------------------------------------------------------------------------------------------------

export interface Face { sfnt: Uint8Array; font: TrueTypeFont; family: string; key: string }

const newFace = (sfnt: Uint8Array, family: string, key: string): Face | null => { try { return { sfnt, font: new TrueTypeFont(sfnt), family, key }; } catch { return null; } };

/** A chosen face and, behind it, the faces that draw the characters it lacks. */
export class FontStack {
    faces: Face[] = [];
    fellBack = new Set<string>();
    missing = new Set<string>();
    constructor(public readonly primary: FontChoice, private readonly bold: boolean, private readonly italic: boolean, private readonly wishClass: { mono: boolean; serif: boolean }) {
        const f = primary.sfnt ? newFace(primary.sfnt, primary.family, `${primary.family}|${primary.bold}|${primary.italic}`) : null;
        if (f) this.faces.push(f);
    }

    private has(face: Face, cp: number): boolean { return face.font.gidByUnicode(cp) > 0; }

    /** Looks every character of `text` up, growing the chain with the bundled face of the same class, Noto Sans, then an installed face. */
    async prepare(text: string): Promise<void> {
        for (const ch of text) {
            const cp = ch.codePointAt(0)!;
            if (cp < 32 || cp === 0xa0 || cp === 0x200b) continue;
            if (this.faces.some((f) => this.has(f, cp))) continue;
            const classFam = this.wishClass.mono ? "Liberation Mono" : this.wishClass.serif ? "Liberation Serif" : "Liberation Sans";
            const cands: Array<[BundledFamily]> = [[classFam as BundledFamily], ["Noto Sans"]];
            let found = false;
            for (const [fam] of cands) {
                const key = `${fam}|${this.bold}|${this.italic}`;
                let face = this.faces.find((f) => f.key === key);
                if (!face) {
                    const b = await loadBundled(fam, this.bold, this.italic);
                    face = (b && newFace(b, fam, key)) || undefined;
                    if (face) this.faces.push(face);
                }
                if (face && this.has(face, cp)) { this.fellBack.add(ch); found = true; break; }
            }
            if (!found) this.missing.add(ch);
        }
    }

    /** The text cut into runs by the face that draws each character (first face in the chain that has it; the primary when none does). */
    runs(text: string): Array<{ face: number; text: string }> {
        const out: Array<{ face: number; text: string }> = [];
        for (const ch of text) {
            const cp = ch.codePointAt(0)!;
            let i = this.faces.findIndex((f) => this.has(f, cp));
            if (i < 0) i = 0;
            const last = out[out.length - 1];
            if (last && last.face === i) last.text += ch; else out.push({ face: i, text: ch });
        }
        return out;
    }

    notice(): string | null {
        const parts: string[] = [];
        if (this.primary.notice) parts.push(this.primary.notice);
        if (this.fellBack.size) parts.push(`${this.fellBack.size} character${this.fellBack.size === 1 ? "" : "s"} (${[...this.fellBack].slice(0, 8).join("")}) ${this.fellBack.size === 1 ? "is" : "are"} drawn in a fallback font.`);
        if (this.missing.size) parts.push(`No available font has ${this.missing.size} character${this.missing.size === 1 ? "" : "s"} (${[...this.missing].slice(0, 8).join("")}).`);
        return parts.length ? parts.join(" ") : null;
    }
}

/** The bundled faces that draw `missing` (characters a text box's font lacks): the face of the box's class, then Noto Sans. Only faces that have some of them. */
export async function fallbackFacesFor(missing: string, bold: boolean, italic: boolean, cls: { mono: boolean; serif: boolean }): Promise<{ faces: Array<{ key: string; family: string; sfnt: Uint8Array }>; stillMissing: string }> {
    let left = [...missing];
    const faces: Array<{ key: string; family: string; sfnt: Uint8Array }> = [];
    const fams: BundledFamily[] = [cls.mono ? "Liberation Mono" : cls.serif ? "Liberation Serif" : "Liberation Sans", "Noto Sans"];
    for (const fam of fams) {
        if (!left.length) break;
        const b = await loadBundled(fam, bold, italic);
        if (!b) continue;
        const f = newFace(b, fam, `${fam}|${bold}|${italic}`);
        if (!f) continue;
        const has = left.filter((ch) => f.font.gidByUnicode(ch.codePointAt(0)!) > 0);
        if (has.length) { faces.push({ key: f.key, family: fam, sfnt: b }); left = left.filter((c) => !has.includes(c)); }
    }
    return { faces, stillMissing: left.join("") };
}

/** A stack for a run of the page: resolves the primary face, then the chain for `text`. */
export async function stackFor(run: RunFontInfo, text: string): Promise<FontStack> {
    const choice = await resolveFont(run, text);
    const wish = wishFromName(run.baseFont, run.flags ?? 0);
    const stack = new FontStack(choice, choice.bold, choice.italic, { mono: wish.mono, serif: wish.serif });
    await stack.prepare(text);
    return stack;
}

export { stripSubsetTag };
